import { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { formatTimeMMSS } from "@/lib/utils";
import { MicIcon, TimerIcon } from "lucide-react";
import { t } from "i18next";
import type { TranscriptionStreamText } from "@/hooks/transcriber/types";
import { Skeleton } from "@/components/ui/skeleton";

interface Props {
  transcriptions: TranscriptionStreamText[];
  loading: boolean;
}

export const TranscriptionList: React.FC<Props> = ({
  transcriptions,
  loading,
}): JSX.Element => {
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [transcriptions, loading]);

  return (
    <div className="flex flex-col gap-2">
      <div className="bg-white rounded-l-lg p-4 h-80 overflow-y-auto border border-gray-200">
        {transcriptions.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400 text-base italic gap-2">
            <div className="flex gap-2">
              <MicIcon className="w-6 h-6" />
              {t("components.transcriptionList.emptyState")}
            </div>
          </div>
        ) : (
          <ul className="space-y-3">
            <AnimatePresence initial={false}>
              {transcriptions.map((item, index) => (
                <motion.li
                  key={item.startTime + "-" + index}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-100 p-3 rounded-xl shadow-sm"
                  exit={{ opacity: 0, y: -10 }}
                  initial={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.25 }}
                >
                  <div className="flex gap-1 text-sm text-gray-600">
                    <TimerIcon className="w-4 h-4" />
                    {formatTimeMMSS(item.startTime)}
                  </div>
                  <div className="text-gray-800 font-medium">{item.text}</div>
                </motion.li>
              ))}
            </AnimatePresence>

            {loading && (
              <li className="bg-gray-100 p-3 rounded-xl shadow-sm space-y-2">
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <TimerIcon className="w-4 h-4 text-gray-400" />
                  <Skeleton className="h-4 w-12" />
                </div>
                <Skeleton className="h-5 w-3/4 rounded-md" />
              </li>
            )}

            <div ref={endRef} />
          </ul>
        )}
      </div>
      <div className="text-tinted-grey-3 text-xs">
        Text post-processed by AI
      </div>
    </div>
  );
};
